/**
 * Authentication calls.
 *
 * These do NOT go through `shared_api_client`. That client prefixes `/api`, retries, and — most
 * importantly — routes 401s into the session-expired guard. A wrong password is a 401 that means
 * "try again", not "your session died"; sending it through the guard would reload the login page
 * out from under the user and throw away the message telling them what went wrong.
 *
 * There is no token here and no session id. The server sets an HttpOnly cookie the browser cannot
 * read, so "am I signed in?" is answered by asking the server, not by inspecting storage.
 */

export interface SessionInfo {
  authenticated: boolean
  userId?: string
  username?: string
  displayName?: string
  email?: string
  clientAccountId?: string
}

export class LoginError extends Error {
  readonly errorCode: string
  readonly status: number

  constructor(message: string, errorCode: string, status: number) {
    super(message)
    this.name = 'LoginError'
    this.errorCode = errorCode
    this.status = status
  }
}

async function parseError(response: Response, fallback: string): Promise<LoginError> {
  let body: any = null
  try {
    body = await response.json()
  } catch {
    body = null
  }
  const detail = body?.detail
  const message =
    (typeof detail?.message === 'string' && detail.message) ||
    (typeof detail === 'string' && detail) ||
    (typeof body?.message === 'string' && body.message) ||
    fallback
  const code = detail?.error_code ?? body?.error_code ?? 'LOGIN_FAILED'
  return new LoginError(message, String(code), response.status)
}

export async function login(username: string, password: string): Promise<SessionInfo> {
  let response: Response
  try {
    response = await fetch('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      //  Same-origin is the default, but stated because the whole design depends on the cookie
      //  travelling — a future move to a separate API origin would need 'include' plus CORS.
      credentials: 'same-origin',
      body: JSON.stringify({ username, password }),
    })
  } catch (networkError) {
    throw new LoginError(
      'Cannot reach Tidalfin. Check your connection and try again.',
      'NETWORK',
      0,
    )
  }
  if (!response.ok) {
    throw await parseError(response, 'That username or password was not recognised.')
  }
  return (await response.json()) as SessionInfo
}

export async function logout(): Promise<void> {
  try {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' })
  } catch {
    //  Best effort. The cookie is cleared server-side when the call lands; if it does not, the
    //  session still expires on its own, and forcing the user back to the login screen is more
    //  useful than an error they cannot act on.
  }
}

export async function fetchSession(): Promise<SessionInfo> {
  try {
    const response = await fetch('/auth/session', { credentials: 'same-origin' })
    if (!response.ok) return { authenticated: false }
    return (await response.json()) as SessionInfo
  } catch {
    //  Treated as signed out rather than as an error: the login page is the one screen that works
    //  without a backend round trip succeeding, so it is the safe place to land.
    return { authenticated: false }
  }
}
